import React, { useState } from 'react';
import { useAuth } from '@/App';
import { useApiData, useApiMutation } from '@/hooks';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  Users, UserPlus, UserMinus, Search, Filter, MoreVertical,
  Edit, Trash2, RefreshCw, CheckCircle, XCircle, AlertTriangle,
  Building2, Briefcase, Phone, Mail, Calendar, Activity
} from 'lucide-react';
import DataFixToolbar from '@/components/common/DataFixToolbar';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Team color mapping
const TEAM_COLORS = {
  'Blue': 'bg-blue-500',
  'Blue Team': 'bg-blue-500',
  'Yellow': 'bg-yellow-500',
  'Yellow Team': 'bg-yellow-500',
  'Green': 'bg-green-500',
  'Green Team': 'bg-green-500',
  'Red': 'bg-red-500',
  'Red Team': 'bg-red-500',
  'Unassigned': 'bg-slate-500'
};

const getTeamColor = (team) => {
  for (const [key, color] of Object.entries(TEAM_COLORS)) {
    if (team?.toLowerCase().includes(key.toLowerCase())) return color;
  }
  return 'bg-slate-500';
};

// Add/Edit Staff Dialog Component
const StaffDialog = ({ staff, teams, disciplines, onSave, onClose, open }) => {
  const [formData, setFormData] = useState({
    name: staff?.name || '',
    discipline: staff?.discipline || 'RN',
    team: staff?.team || 'Unassigned',
    email: staff?.email || '',
    phone: staff?.phone || '',
    hire_date: staff?.hire_date || '',
    notes: staff?.notes || ''
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      toast.error('Staff name is required');
      return;
    }
    setSaving(true);
    try {
      await onSave(formData, staff?.id);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg bg-slate-900 border-slate-700">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-white font-bold">
            {staff ? <Edit className="h-5 w-5 text-cyan-400" /> : <UserPlus className="h-5 w-5 text-emerald-400" />}
            {staff ? 'Edit Staff Member' : 'Add New Staff Member'}
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            {staff ? 'Update staff member details' : 'Enter details for the new staff member'}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label className="text-slate-300 font-bold">Full Name *</Label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., Sarah Johnson"
              className="bg-slate-800 border-slate-600 text-white"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label className="text-slate-300 font-bold">Discipline</Label>
              <Select value={formData.discipline} onValueChange={(v) => setFormData({ ...formData, discipline: v })}>
                <SelectTrigger className="bg-slate-800 border-slate-600 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-600">
                  {disciplines.map(d => (
                    <SelectItem key={d} value={d} className="text-white">{d}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid gap-2">
              <Label className="text-slate-300 font-bold">Team</Label>
              <Select value={formData.team} onValueChange={(v) => setFormData({ ...formData, team: v })}>
                <SelectTrigger className="bg-slate-800 border-slate-600 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-600">
                  {teams.map(t => (
                    <SelectItem key={t} value={t} className="text-white">{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label className="text-slate-300 font-bold">Email</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="email@example.com"
                className="bg-slate-800 border-slate-600 text-white"
              />
            </div>

            <div className="grid gap-2">
              <Label className="text-slate-300 font-bold">Phone</Label>
              <Input
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="(555) 123-4567"
                className="bg-slate-800 border-slate-600 text-white"
              />
            </div>
          </div>

          <div className="grid gap-2">
            <Label className="text-slate-300 font-bold">Hire Date</Label>
            <Input
              type="date"
              value={formData.hire_date}
              onChange={(e) => setFormData({ ...formData, hire_date: e.target.value })}
              className="bg-slate-800 border-slate-600 text-white"
            />
          </div>

          <div className="grid gap-2">
            <Label className="text-slate-300 font-bold">Notes</Label>
            <Input
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Additional notes..."
              className="bg-slate-800 border-slate-600 text-white"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-slate-600 text-slate-300">
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={saving}
            className="bg-gradient-to-r from-cyan-500 to-blue-600"
          >
            {saving ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : null}
            {staff ? 'Update Staff' : 'Add Staff'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Delete Confirmation Dialog
const DeleteDialog = ({ staff, onConfirm, onClose, open }) => {
  const [permanent, setPermanent] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await onConfirm(staff.id || staff.name, permanent);
      onClose();
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md bg-slate-900 border-slate-700">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-white font-bold">
            <Trash2 className="h-5 w-5 text-rose-400" />
            Remove Staff Member
          </DialogTitle>
        </DialogHeader>

        <div className="py-4">
          <p className="text-slate-300">
            Are you sure you want to remove <span className="font-bold text-white">{staff?.name}</span>?
          </p>
          
          <div className="mt-4 flex items-center gap-2">
            <input
              type="checkbox"
              id="permanent"
              checked={permanent}
              onChange={(e) => setPermanent(e.target.checked)}
              className="rounded border-slate-600"
            />
            <Label htmlFor="permanent" className="text-slate-400 text-sm">
              Permanently delete (including all productivity records)
            </Label>
          </div>

          {permanent && (
            <Alert className="mt-4 bg-rose-900/20 border-rose-600/30">
              <AlertTriangle className="h-4 w-4 text-rose-400" />
              <AlertDescription className="text-rose-200 text-sm">
                This will permanently delete all records for this staff member. This action cannot be undone.
              </AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-slate-600 text-slate-300">
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            disabled={deleting}
            variant="destructive"
            className="bg-rose-600 hover:bg-rose-700"
          >
            {deleting ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
            {permanent ? 'Delete Permanently' : 'Mark Inactive'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Staff Card Component
const StaffCard = ({ staff, onEdit, onDelete }) => {
  const initials = staff.name?.split(' ').map(n => n[0]).join('').slice(0, 2) || '?';
  
  return (
    <Card className="bg-slate-800/50 border-slate-700/50 hover:bg-slate-800 transition-all">
      <CardContent className="p-4">
        <div className="flex items-start gap-4">
          <Avatar className="h-12 w-12">
            <AvatarFallback className={`${getTeamColor(staff.team)} text-white font-bold`}>
              {initials}
            </AvatarFallback>
          </Avatar>
          
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="font-bold text-white truncate">{staff.name}</h3>
              {staff.status === 'inactive' && (
                <Badge variant="outline" className="border-rose-500/50 text-rose-400 text-xs">Inactive</Badge>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              <Badge className="bg-slate-700 text-slate-300">{staff.discipline}</Badge>
              <Badge className={`${getTeamColor(staff.team)} text-white`}>{staff.team || 'Unassigned'}</Badge>
            </div>
            
            {staff.visits_completed !== undefined && (
              <div className="mt-2 text-sm text-slate-400">
                <span className="text-cyan-400 font-bold">{staff.visits_completed}</span> visits • 
                <span className="text-emerald-400 font-bold ml-1">{staff.current_case_count || 0}</span> patients
              </div>
            )}
          </div>
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-400 hover:text-white">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-slate-800 border-slate-700">
              <DropdownMenuItem onClick={() => onEdit(staff)} className="text-white hover:bg-slate-700">
                <Edit className="h-4 w-4 mr-2" /> Edit
              </DropdownMenuItem>
              <DropdownMenuSeparator className="bg-slate-700" />
              <DropdownMenuItem onClick={() => onDelete(staff)} className="text-rose-400 hover:bg-slate-700">
                <Trash2 className="h-4 w-4 mr-2" /> Remove
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </CardContent>
    </Card>
  );
};

// Main Staff Management Page
export default function StaffManagement() {
  const { token } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [teamFilter, setTeamFilter] = useState('all');
  const [disciplineFilter, setDisciplineFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('active');
  
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  const [deletingStaff, setDeletingStaff] = useState(null);

  // Fetch staff data
  const { data: staffData, loading, refetch } = useApiData(
    `/api/staff?status=${statusFilter}`,
    { cache: false }
  );
  
  const { data: statsData } = useApiData('/api/staff/stats', { cache: true });
  const { data: teamsData } = useApiData('/api/staff/teams', { cache: true });
  const { data: disciplinesData } = useApiData('/api/staff/disciplines', { cache: true });

  const staff = staffData?.staff || [];
  const teams = teamsData?.teams || ['Blue Team', 'Yellow Team', 'Green Team', 'Unassigned'];
  const disciplines = disciplinesData?.disciplines || ['RN', 'LPN', 'RNCM', 'SW', 'Chaplain', 'HHA', 'CNA'];
  const stats = statsData || {};

  // Filter staff
  const filteredStaff = staff.filter(s => {
    const matchesSearch = !searchQuery || 
      s.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      s.discipline?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesTeam = teamFilter === 'all' || s.team?.toLowerCase().includes(teamFilter.toLowerCase());
    const matchesDiscipline = disciplineFilter === 'all' || s.discipline === disciplineFilter;
    return matchesSearch && matchesTeam && matchesDiscipline;
  });

  const getAuthHeaders = () => ({
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json'
  });

  // Add staff
  const handleAddStaff = async (formData) => {
    const res = await fetch(`${API_URL}/api/staff`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(formData)
    });
    
    if (res.ok) {
      toast.success('Staff member added successfully');
      refetch();
    } else {
      const error = await res.json();
      toast.error(error.detail || 'Failed to add staff member');
      throw new Error(error.detail);
    }
  };

  // Update staff
  const handleUpdateStaff = async (formData, staffId) => {
    const res = await fetch(`${API_URL}/api/staff/${staffId}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(formData)
    });
    
    if (res.ok) {
      toast.success('Staff member updated successfully');
      refetch();
    } else {
      const error = await res.json();
      toast.error(error.detail || 'Failed to update staff member');
      throw new Error(error.detail);
    }
  };

  // Delete staff
  const handleDeleteStaff = async (staffId, permanent) => {
    const res = await fetch(`${API_URL}/api/staff/${staffId}?permanent=${permanent}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    
    if (res.ok) {
      toast.success(permanent ? 'Staff member permanently deleted' : 'Staff member marked as inactive');
      refetch();
    } else {
      const error = await res.json();
      toast.error(error.detail || 'Failed to remove staff member');
      throw new Error(error.detail);
    }
  };

  return (
    <div className="space-y-6" data-testid="staff-management-page">
      {/* Futuristic Header */}
      <div className="relative overflow-hidden rounded-3xl p-8 text-white">
        <div className="absolute inset-0 bg-gradient-to-r from-indigo-600 via-violet-600 to-purple-600 animate-gradient-x" />
        <div className="absolute inset-0 opacity-20" style={{
          backgroundImage: `radial-gradient(circle at 25% 25%, white 1px, transparent 1px),
                            radial-gradient(circle at 75% 75%, white 1px, transparent 1px)`,
          backgroundSize: '60px 60px'
        }} />
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-white/20 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-white/15 rounded-full blur-3xl" />

        <div className="relative z-10 flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-black tracking-tight flex items-center gap-4">
              <div className="p-3 bg-white/20 rounded-2xl backdrop-blur-sm shadow-lg">
                <Users className="h-8 w-8" />
              </div>
              <span className="drop-shadow-lg">Staff Management</span>
            </h1>
            <p className="text-white/80 mt-3 text-lg font-medium">
              Manage your team members, assignments, and roles
            </p>
          </div>
          
          <Button
            onClick={() => setShowAddDialog(true)}
            className="bg-white/20 hover:bg-white/30 text-white border-0 gap-2"
            size="lg"
          >
            <UserPlus className="h-5 w-5" />
            Add Staff
          </Button>
        </div>
      </div>

      {/* Data Fix Toolbar */}
      <DataFixToolbar onDataFixed={refetch} variant="compact" />

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-slate-900/80 backdrop-blur-xl border-indigo-500/30" style={{ boxShadow: '0 0 20px rgba(99, 102, 241, 0.2)' }}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-gradient-to-r from-indigo-500 to-violet-500 rounded-xl text-white">
                <Users className="h-6 w-6" />
              </div>
              <div>
                <p className="text-sm text-indigo-400 font-bold">Total Active</p>
                <p className="text-2xl font-black text-white">{stats.total_active || filteredStaff.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-slate-900/80 backdrop-blur-xl border-emerald-500/30" style={{ boxShadow: '0 0 20px rgba(16, 185, 129, 0.2)' }}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-gradient-to-r from-emerald-500 to-green-500 rounded-xl text-white">
                <Building2 className="h-6 w-6" />
              </div>
              <div>
                <p className="text-sm text-emerald-400 font-bold">Teams</p>
                <p className="text-2xl font-black text-white">{Object.keys(stats.by_team || {}).length || teams.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-slate-900/80 backdrop-blur-xl border-cyan-500/30" style={{ boxShadow: '0 0 20px rgba(6, 182, 212, 0.2)' }}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-gradient-to-r from-cyan-500 to-teal-500 rounded-xl text-white">
                <Briefcase className="h-6 w-6" />
              </div>
              <div>
                <p className="text-sm text-cyan-400 font-bold">Disciplines</p>
                <p className="text-2xl font-black text-white">{Object.keys(stats.by_discipline || {}).length || disciplines.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-slate-900/80 backdrop-blur-xl border-rose-500/30" style={{ boxShadow: '0 0 20px rgba(244, 63, 94, 0.2)' }}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-gradient-to-r from-rose-500 to-pink-500 rounded-xl text-white">
                <UserMinus className="h-6 w-6" />
              </div>
              <div>
                <p className="text-sm text-rose-400 font-bold">Inactive</p>
                <p className="text-2xl font-black text-white">{stats.total_inactive || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="bg-slate-900/60 backdrop-blur-xl border-slate-700/50">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search staff..."
                  className="pl-10 bg-slate-800 border-slate-600 text-white"
                />
              </div>
            </div>
            
            <Select value={teamFilter} onValueChange={setTeamFilter}>
              <SelectTrigger className="w-[160px] bg-slate-800 border-slate-600 text-white">
                <SelectValue placeholder="Filter by team" />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-600">
                <SelectItem value="all" className="text-white">All Teams</SelectItem>
                {teams.map(t => (
                  <SelectItem key={t} value={t.toLowerCase()} className="text-white">{t}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={disciplineFilter} onValueChange={setDisciplineFilter}>
              <SelectTrigger className="w-[160px] bg-slate-800 border-slate-600 text-white">
                <SelectValue placeholder="Filter by discipline" />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-600">
                <SelectItem value="all" className="text-white">All Disciplines</SelectItem>
                {disciplines.map(d => (
                  <SelectItem key={d} value={d} className="text-white">{d}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[140px] bg-slate-800 border-slate-600 text-white">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-600">
                <SelectItem value="active" className="text-white">Active</SelectItem>
                <SelectItem value="inactive" className="text-white">Inactive</SelectItem>
                <SelectItem value="all" className="text-white">All</SelectItem>
              </SelectContent>
            </Select>

            <Button variant="outline" onClick={refetch} className="border-slate-600 text-slate-300 gap-2">
              <RefreshCw className="h-4 w-4" /> Refresh
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Staff Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-32" />)}
        </div>
      ) : filteredStaff.length === 0 ? (
        <Card className="bg-slate-900/60 backdrop-blur-xl border-slate-700/50">
          <CardContent className="p-12 text-center">
            <Users className="h-16 w-16 text-slate-500 mx-auto mb-4" />
            <p className="text-lg font-bold text-white">No Staff Found</p>
            <p className="text-slate-400 mt-2">
              {searchQuery || teamFilter !== 'all' || disciplineFilter !== 'all'
                ? 'Try adjusting your filters'
                : 'Add your first staff member to get started'}
            </p>
            <Button onClick={() => setShowAddDialog(true)} className="mt-4 gap-2">
              <UserPlus className="h-4 w-4" /> Add Staff
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredStaff.map((s, idx) => (
            <StaffCard
              key={s.id || idx}
              staff={s}
              onEdit={() => setEditingStaff(s)}
              onDelete={() => setDeletingStaff(s)}
            />
          ))}
        </div>
      )}

      {/* Summary by Team */}
      {stats.by_team && Object.keys(stats.by_team).length > 0 && (
        <Card className="bg-slate-900/60 backdrop-blur-xl border-slate-700/50">
          <CardHeader>
            <CardTitle className="text-white font-bold flex items-center gap-2">
              <Building2 className="h-5 w-5 text-violet-400" />
              Staff by Team
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-4">
              {Object.entries(stats.by_team).map(([team, count]) => (
                <div key={team} className="flex items-center gap-2">
                  <div className={`w-3 h-3 rounded-full ${getTeamColor(team)}`} />
                  <span className="text-slate-300 font-medium">{team}:</span>
                  <span className="text-white font-bold">{count}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Dialogs */}
      <StaffDialog
        staff={editingStaff}
        teams={teams}
        disciplines={disciplines}
        onSave={editingStaff ? handleUpdateStaff : handleAddStaff}
        onClose={() => { setShowAddDialog(false); setEditingStaff(null); }}
        open={showAddDialog || !!editingStaff}
      />

      <DeleteDialog
        staff={deletingStaff}
        onConfirm={handleDeleteStaff}
        onClose={() => setDeletingStaff(null)}
        open={!!deletingStaff}
      />
    </div>
  );
}
